// src/hooks/useUnsavedReminder.js
// Lightweight helper to nudge users to press Send/Save on dirty forms and guard unsaved exits.
import { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload } from "react-router-dom";

/**
 * @param {Object} opts
 * @param {boolean} opts.isDirty - Whether the form has unsaved edits.
 * @param {() => (void|Promise<void>)} [opts.onConfirmSave] - Called when user chooses "Send & Save".
 * @param {number} [opts.idleMs=45000] - Idle time before showing reminder.
 * @param {string} [opts.message] - Custom reminder copy.
 */
export function useUnsavedReminder({
  isDirty,
  onConfirmSave,
  idleMs = 45000,
  message = "Almost there — tap Send so Mudo can save this.",
} = {}) {
  const [shouldShowReminder, setShouldShowReminder] = useState(false);
  const idleTimer = useRef(null);
  const hasToastedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  }, []);

  const triggerReminder = useCallback(() => {
    setShouldShowReminder(true);
    if (!hasToastedRef.current) {
      try {
        window.dispatchEvent(
          new CustomEvent("mudo:toast", {
            detail: { type: "info", message },
          })
        );
        hasToastedRef.current = true;
      } catch {}
    }
  }, [message]);

  // Reset idle timer when dirty changes
  useEffect(() => {
    clearTimer();
    hasToastedRef.current = false;
    setShouldShowReminder(false);
    if (!isDirty) return undefined;
    idleTimer.current = setTimeout(triggerReminder, idleMs);
    return () => clearTimer();
  }, [isDirty, idleMs, triggerReminder, clearTimer]);

  // Before unload guard for browser/tab close
  useBeforeUnload(
    isDirty
      ? (event) => {
          event.preventDefault();
          event.returnValue = "";
        }
      : undefined
  );

  const registerActivity = useCallback(() => {
    if (!isDirty) return;
    setShouldShowReminder(false);
    hasToastedRef.current = false;
    clearTimer();
    idleTimer.current = setTimeout(triggerReminder, idleMs);
  }, [clearTimer, idleMs, isDirty, triggerReminder]);

  const markSaved = useCallback(() => {
    clearTimer();
    setShouldShowReminder(false);
    hasToastedRef.current = false;
  }, [clearTimer]);

  const confirmBeforeClose = useCallback(async () => {
    if (!isDirty) return true;
    const wantsSave = window.confirm(
      "You have changes that haven't been sent yet.\n\nPress OK to Send & Save, or Cancel to discard."
    );
    if (wantsSave) {
      try {
        await onConfirmSave?.();
      } catch (err) {
        console.error("[unsavedReminder] onConfirmSave error", err);
        return false;
      }
    }
    return true; // OK or discard both allow closing
  }, [isDirty, onConfirmSave]);

  return {
    shouldShowReminder,
    registerActivity,
    markSaved,
    dismissReminder: () => setShouldShowReminder(false),
    confirmBeforeClose,
  };
}

export default useUnsavedReminder;
